/**
 * AssistantConsentPanel (PRP-MCP-9 work item 6, founder decision F7): the
 * consent a user accepts before any chat app tool call works.
 *
 * Shows the server's consent text (`GET /mcp/consent`, the text is data in
 * `app/data/assistant_consent.json`) with an Accept button. After acceptance it
 * shows the same text and the date accepted. A new version of the text on the
 * server reads as not accepted, so it asks again. Rendered inside
 * McpConnectSection, so it disappears with that section when MCP is off.
 */
import { useEffect, useState } from 'react';

import { McpAPI } from '../../lib/mcpApi';
import type { McpConsent } from '../../lib/mcpApi';
import type { ApiError } from '../../types';
import { Button } from './ui/button';
import { Pill } from './ui/pill';

/** Formats an ISO timestamp as a short local date. */
function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/** The consent text with Accept, or with the date it was accepted. */
export default function AssistantConsentPanel() {
  const [consent, setConsent] = useState<McpConsent | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    McpAPI.getConsent()
      .then((c) => {
        if (!cancelled) setConsent(c);
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err?.message || 'Could not load the assistant terms.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accept = async () => {
    if (!consent) return;
    setBusy(true);
    setError(null);
    try {
      setConsent(await McpAPI.acceptConsent(consent.version));
    } catch (err) {
      setError((err as ApiError).message || 'Could not record your acceptance. Try again.');
      try {
        setConsent(await McpAPI.getConsent()); // a 409 means the text changed: show the new one
      } catch {
        /* keep the error shown above */
      }
    } finally {
      setBusy(false);
    }
  };

  if (!consent) {
    return error ? (
      <p className="m-0 text-[12.5px] text-[var(--tx-mid)]" role="alert">
        {error}
      </p>
    ) : null;
  }

  return (
    <div
      data-testid="assistant-consent"
      className={
        'space-y-2 p-3 rounded-[var(--r-md)] border ' +
        (consent.accepted
          ? 'border-[var(--bd)] bg-[var(--bg-inset)]'
          : 'border-[var(--accent-line)] bg-[var(--accent-soft)]')
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-[550] text-[var(--tx-hi)]">How we use your estimates</span>
        {consent.accepted ? (
          <Pill kind="ok">{`Accepted ${fmtDate(consent.accepted_at)}`}</Pill>
        ) : (
          <Pill kind="info">Needed before your chat app can use AIEA</Pill>
        )}
      </div>
      <p className="m-0 text-[12.5px] text-[var(--tx-mid)]" data-testid="assistant-consent-text">
        {consent.text}
      </p>
      {error && (
        <p className="m-0 text-[12px] text-[var(--tx-mid)]" role="alert">
          {error}
        </p>
      )}
      {!consent.accepted && (
        <Button variant="primary" size="sm" disabled={busy} onClick={() => void accept()}>
          Accept
        </Button>
      )}
    </div>
  );
}
